// Which pre-auth screen a signed-out visitor lands on.
//
// The root used to be the login form. It is now the sales page, and that shift
// has one sharp edge: Supabase sends people BACK to the root. `signUp` uses
// `emailRedirectTo: <origin>/` and `resetPassword` uses `redirectTo: <origin>/`,
// so a confirmation or recovery link returns here carrying its token.
//
// When the token is good, supabase-js adopts a session before this ever
// matters and the app renders. When it is expired or already used, no session
// appears — and without this the visitor would land on a marketing page with
// no hint of what went wrong. So a URL that looks like an auth callback opens
// the login form instead.
//
// Pure on purpose: it takes a URL and returns a decision, so the branch that
// decides what a returning customer sees is testable without a browser.

export type PreAuthView =
  | { view: 'sales' }
  | { view: 'login'; mode: 'in' | 'up' };

/** Paths that address the login form directly, so it can be linked. */
const SIGN_IN_PATHS = new Set(['/entrar', '/login']);
const SIGN_UP_PATHS = new Set(['/criar-conta', '/cadastro', '/signup']);

/**
 * Query and hash keys Supabase returns on. `code` is the PKCE exchange,
 * `access_token` / `type` the implicit flow, and the `error*` pair is what an
 * expired link comes back as — the case this whole function exists for.
 */
const AUTH_CALLBACK_KEYS = ['code', 'access_token', 'refresh_token', 'type', 'error', 'error_code', 'error_description'];

function carriesAuthCallback(url: URL): boolean {
  for (const key of AUTH_CALLBACK_KEYS) {
    if (url.searchParams.has(key)) return true;
  }
  // Supabase's implicit flow puts them after the '#', which `searchParams`
  // does not see.
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) return false;
  const fromHash = new URLSearchParams(hash);
  return AUTH_CALLBACK_KEYS.some((key) => fromHash.has(key));
}

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

export function resolvePreAuthView(href: string): PreAuthView {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { view: 'sales' };
  }
  const path = normalizePath(url.pathname);
  if (SIGN_UP_PATHS.has(path)) return { view: 'login', mode: 'up' };
  if (SIGN_IN_PATHS.has(path)) return { view: 'login', mode: 'in' };
  if (carriesAuthCallback(url)) return { view: 'login', mode: 'in' };
  return { view: 'sales' };
}

/** Browser entry point; safe to call during static export, where it is 'sales'. */
export function initialPreAuthView(): PreAuthView {
  if (typeof window === 'undefined') return { view: 'sales' };
  return resolvePreAuthView(window.location.href);
}
